import { FC, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Menu as AntMenu, MenuProps, Button } from 'antd'
import { DownloadOutlined, DownOutlined } from '@ant-design/icons'
import { useDialog } from 'src/shared/hooks'
import type SwiperCore from 'swiper'

import config from 'src/config/app'
import { MenuHomeStyled, MenuOtherPageStyled } from './styles'
import { MenuPanel } from './MenuPanel'
import { useHomeAndScroll } from 'src/shared/hooks/useHomeandScroll'
import { useListFilters } from './hooks/useListFilters'
import { RequirementFormModal } from 'src/shared/components/RequirementFormModal'
import { ProductsPanelContent } from './MenuPanel/ProductsPanelContent'

interface IProps {
  modalReq: ReturnType<typeof useDialog>
  swiperInstance?: SwiperCore | null
}

const Menu: FC<IProps> = (props) => {
  const [menuRect, setMenuRect] = useState<DOMRect>()
  const { isHome, scrolled } = useHomeAndScroll()
  const menuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const filters = useListFilters()
  const productPanel = useDialog()

  useEffect(() => {
    const swiper = props.swiperInstance
    if (!swiper) return

    const disableSwiperScroll = () => {
      swiper.mousewheel?.disable()
      swiper.keyboard?.disable()
      swiper.allowTouchMove = false
      document.body.style.overflow = 'hidden'
      swiper.update()
    }

    const enableSwiperScroll = () => {
      swiper.mousewheel?.enable()
      swiper.keyboard?.enable()
      swiper.allowTouchMove = true
      document.body.style.overflow = ''
      swiper.update()
    }

    if (productPanel.open) {
      disableSwiperScroll()
    } else {
      enableSwiperScroll()
    }

    return () => {
      enableSwiperScroll()
    }
  }, [productPanel.open, props.swiperInstance])

  const selectedKey = pathname === '/' ? '/homev2' : pathname

  const items: MenuProps['items'] = [
    {
      label: 'Nosotros',
      key: '/aboutus',
      onClick: () => navigate('/aboutus'),
    },
    {
      label: (
        <div>
          <Button onClick={() => navigate('/products')} type="text" icon={<DownOutlined />}>
            Productos
          </Button>
          <MenuPanel
            isOpen={productPanel.open}
            style={{
              top: 100, // GAB-01-Menu
              height: `calc(100vh - ${menuRect?.bottom}px)`,
              borderRadius: 16,
            }}
            content={
              <ProductsPanelContent
                categories={filters.categories ?? []}
                brandsByCategory={filters.brands}
              />
            }
            onMouseEnter={() => {
              if (props.swiperInstance) {
                productPanel.handleOpen()
              }
            }}
            onMouseLeave={() => {
              if (props.swiperInstance) {
                productPanel.handleClose()
              }
            }}
          />
        </div>
      ),
      key: '/products',
      onMouseEnter: productPanel.handleOpen,
      onMouseLeave: productPanel.handleClose,
    },
    {
      label: (
        <a target="_blank" href={`${config.files.storePDF}`}>
          <Button type="text" icon={<DownloadOutlined />}>
            Catálogo
          </Button>
        </a>
      ),
      key: '/catalogo',
    },
  ]

  useEffect(() => {
    if (menuRef.current) {
      setMenuRect(menuRef.current.getBoundingClientRect())
    }
  }, [])

  const MenuComponent = isHome && !scrolled ? MenuHomeStyled : MenuOtherPageStyled

  return (
    <MenuComponent ref={menuRef}>
      <AntMenu
        inlineCollapsed={false}
        items={items}
        mode="horizontal"
        selectedKeys={[selectedKey]}
      />

      <RequirementFormModal
        onSuccess={props.modalReq.handleClose}
        open={props.modalReq.open}
        handleClose={props.modalReq.handleClose}
      />
    </MenuComponent>
  )
}

export default Menu
